import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  getRelativePosition,
  useGetWriting,
  useUpdateWriting,
} from "@/api/writing/writing";
import {
  appendNoDuplicateRecordNumberRefine,
  writingSchema,
} from "@/api/writing/writing-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { TwoDimensionalInput } from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { formatFormRegistrationReference } from "@/lib/form-registration-header";
import { computeFieldWritingDescription } from "@/lib/computeFields";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { Writing } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateRecordNumberRefine(writingSchema.passthrough());

const EditWriting = ({ writing }: { writing: Writing }) => {
  // Rafraîchit le tableau des fichiers après un ajout réussi dans AddFileModal.
  const [refreshFiles, setRefreshFiles] = useState(false);

  const { instanceData } = useAuth();

  const isModuleSculptureEnabled = useMemo(() => {
    return instanceData?.isModuleSculptureEnabled;
  }, [instanceData?.isModuleSculptureEnabled]);

  const { data: attachments } = useGetAttachmentsByDocId(writing.id);

  const form = useForm({
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: writing,
  });

  const writingUpdater = useUpdateWriting();

  const { t, i18n } = useTranslation();
  const vt = useVisibleT();

  const handleLegendeRedigee = (data: Writing) => {
    return computeFieldWritingDescription(data, {
      language: getMainLanguage(),
    });
  };

  const { relativePosition } = useDashboardParams<Writing>({
    statusField: "ecritsPublicationStatus",
    resourceName: "writing",
    relativePosition: {
      currentId: writing.id,
      positionFetcher: getRelativePosition,
    },
  });

  const queryClient = useQueryClient();

  const [, setSearchParams] = useSearchParams();

  const ecritsDescriptionNumeroEnregistrement = form.watch(
    "ecritsDescriptionNumeroEnregistrement",
  );
  const registrationLabel = t("backoffice.header.registrationNumber.label");
  const registrationReference = useMemo(
    () => formatFormRegistrationReference(ecritsDescriptionNumeroEnregistrement, {
      label: registrationLabel,
      locale: i18n.language,
    }),
    [ecritsDescriptionNumeroEnregistrement, registrationLabel, i18n.language],
  );

  return (
    <FormLayout
      formKey="writing"
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            writingUpdater.mutate(
              {
                ...values,
                ...(status && { ecritsPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("writingSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.ecritsDescriptionNumeroEnregistrement != undefined)
              setSearchParams({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({ queryKey: ["writing", writing.id] });
        queryClient.invalidateQueries({ queryKey: ["writings"] });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: writing.id }],
        });
      }}
      relativePosition={relativePosition}
      publicationStatus={writing.ecritsPublicationStatus}
      title={writing.ecritsDescriptionTitre}
      reference={registrationReference}
      id={writing.id}
      type="writing"
      picturesData={
        attachments && {
          pictures: attachments,
          type: "writing",
          size: "mediumPublicUrl",
        }
      }
      form={form}
    >
      <AF.AbstractForm
        form={form}
        formKey="writing"
        tabs={[
          { nonHideable: true, value: "description", label: t("writingDescription") },
          { value: "retranscription", label: t("retranscription") },
          { value: "sources", label: t("sources") },
          { value: "files", label: t("fichiers") },
          { value: "links", label: t("linkedEntries") },
        ]}
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Row className="items-center [&>*]:min-w-0">
              <div className="w-full min-w-0">
                <AF.Text
                  name="ecritsDescriptionNumeroEnregistrement"
                  nonMaskable
                />
              </div>
              <AF.Checkbox name="ecritsDescriptionCopies" align={true} className="pt-6" />
            </AF.Row>
            <ModifiableList
              name="ecritsDescriptionType"
              listKey="liste-types-document"
            />
            <AF.Row className="items-start gap-6 [&>*]:min-w-0">
              <div className="w-1/2 min-w-0">
                <AF.Text name="ecritsDescriptionClassement" />
              </div>
              <div className="w-1/2 min-w-0">
                <AF.Text name="ecritsDescriptionRangement" />
              </div>
            </AF.Row>
            <ModifiableList
              name="ecritsDescriptionLocalisation"
              listKey="liste-bibliotheques-archives"
            />
            <div className="w-[calc(50%-12px)] min-w-0">
              <AF.Text name="ecritsDescriptionNombreExemplairesArchives" />
            </div>
            <AF.Text
              name="ecritsDescriptionTitre"
              nonMaskable
              expandOnOverflow
              className="[field-sizing:content] min-h-9 resize-none"
            />
            <ModifiableList
              name="ecritsDescriptionLangueDOrigine"
              listKey="liste-langues"
              label={vt("ecritsDescriptionLangueDOrigine", "Langue d'origine")}
            />
            <AF.Row className="items-start [&>*]:min-w-0">
              <div className="w-[calc(50%-12px)]">
                <AF.Text name="ecritsDescriptionDate" />
              </div>
              <div className="min-w-0 flex-1">
                <ModifiableList
                  name="ecritsDescriptionTheme"
                  listKey="liste-themes"
                />
              </div>
            </AF.Row>
            <TwoDimensionalInput parentName="ecritsDescriptionDimensions" />
            <ModifiableList
              name="ecritsDescriptionTechnique"
              listKey="liste-techniques-document"
            />
            <AF.Text name="ecritsDescriptionLieuDeCreation" />
            <ModifiableList
              name="ecritsDescriptionPhotographe"
              listKey="liste-photographes"
            />
            <ModifiableList
              name="ecritsDescriptionCopyright"
              listKey="liste-credits"
            />
            <AF.Text className="w-16" name="ecritsDescriptionNombreFeuillet" />
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="ecritsDescriptionLegendeRedigee"
              computeAction={handleLegendeRedigee}
            />
            <AF.RichText name="ecritsDescriptionNotes" />
            <AF.Row>
              <AF.Checkbox name="ecritsFichierPhotoBasseDefinitionUniquement" />
              <AF.Checkbox name="ecritsFichierPhotoScanAFaire" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="ecritsFichierPhotoOkWeb" />
              <AF.Checkbox name="ecritsFichierPhotoHdOk" />
            </AF.Row>
            <Separator />
            <AF.Row>
              <AF.Checkbox name="ecritsValidationPublicationNumerisee" />
              <AF.Checkbox name="ecritsValidationPdfEtOcrOk" />
            </AF.Row>
            <Separator />
            <AF.RadioGroup
              resettable={true}
              items={[
                { value: "ecritsAcquisitionDocumentAAcquerir" },
                {
                  value: "ecritsAcquisitionDocumentAAcquis",
                },
              ]}
            />
            <AF.Row>
              <AF.Text name="ecritsAcquisitionDateAcquisition" />
              <AF.Text name="ecritsAcquisitionVendeur" />
            </AF.Row>
            <AF.RichText name="ecritsAcquisitionNotes" />
            <AF.Text name="ecritsMouvementsDepot" />
            <AF.RichText name="ecritsMouvementsNotesDepot" />
            <AF.LegacySelectCurrency name="ecritsMouvementsValeurAssurance" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="retranscription">
          <AF.RichText name="ecritsDescriptionRetranscription" />
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          value="files"
          headerExtras={
            <AddFileModal
              id={writing.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            name="diversFiles"
            docId={writing.id}
            refreshFiles={refreshFiles}
            setRefreshFiles={setRefreshFiles}
          />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={writing.id}
            type="writing"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedSculptureEditions",
                      label: t("entrysculptureeditions"),
                      type: "link-simple",
                      hidden: !isModuleSculptureEnabled,
                    },
                    {
                      name: "relatedMultipleprint",
                      label: t("entrymultipleprints"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedMultipleSample",
                      label: t("entrymultiplesamples"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedBibliography",
                      label: t("entrybibliographies"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
            data={writing}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditWritingPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetWriting(id);

  return (
    <>
      {data && <EditWriting key={data.id} writing={data} />}
      {!data && !isLoading && <h2 className="p-4">ecritNExistePasLabel </h2>}
    </>
  );
};
